import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Eye, Tag, ArrowRight, Mail, X, Instagram, Facebook, Linkedin, TrendingUp, Brain, Shield, Heart, MessageSquare, SortAsc, Search, Users, Share2, Twitter, Link as LinkIcon, Check, LogIn, Crown, Flame } from 'lucide-react';
import axios from 'axios';
import LanguagePicker, { useTranslation, translateText, t } from '../components/LanguagePicker';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
  return url;
};

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [email, setEmail] = useState('');
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [subMsg, setSubMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const { lang } = useTranslation();
  const { user } = useAuth();
  const [translatedArticles, setTranslatedArticles] = useState({});
  const [exclusiveTeasers, setExclusiveTeasers] = useState([]);
  const [trendingTags, setTrendingTags] = useState([]);

  useEffect(() => { fetchArticles(); fetchTrendingTags(); }, []);

  // Homepage SEO meta tags + JSON-LD
  useEffect(() => {
    document.title = 'Pakde Heru — Strategic Thinker | Behavioral Finance & Wealth Logic';
    const seoTags = [
      { name: 'description', content: 'Blog Pakde Heru — Insight tajam tentang behavioral finance, wealth logic, dan psikologi uang. Berhenti mengikut arus, mulai pahami logika di balik setiap keputusan finansial.' },
      { name: 'keywords', content: 'pakde heru, behavioral finance, wealth logic, psikologi uang, bias kognitif, investasi syariah, literasi keuangan, strategic thinker' },
      { name: 'author', content: 'Pakde Heru' },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: 'Pakde Heru — Strategic Thinker' },
      { property: 'og:description', content: 'Insight tajam tentang behavioral finance, wealth logic, dan psikologi uang dari Pakde Heru.' },
      { property: 'og:site_name', content: 'Pakde Heru — Strategic Thinker' },
      { property: 'og:locale', content: 'id_ID' },
      { property: 'og:url', content: window.location.origin },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Pakde Heru — Strategic Thinker' },
      { name: 'twitter:description', content: 'Insight tajam tentang behavioral finance, wealth logic, dan psikologi uang.' },
    ];
    const created = [];
    seoTags.forEach(({ property, name, content }) => {
      const sel = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement('meta'); if (property) el.setAttribute('property', property); if (name) el.setAttribute('name', name); document.head.appendChild(el); created.push(el); }
      el.setAttribute('content', content);
    });
    // JSON-LD for WebSite
    let ld = document.getElementById('homepage-jsonld');
    if (!ld) { ld = document.createElement('script'); ld.id = 'homepage-jsonld'; ld.type = 'application/ld+json'; document.head.appendChild(ld); created.push(ld); }
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'WebSite',
      name: 'Pakde Heru — Strategic Thinker', url: window.location.origin,
      description: 'Blog tentang behavioral finance, wealth logic, dan psikologi uang.',
      author: { '@type': 'Person', name: 'Pakde Heru', url: 'https://facebook.com/pakdeheruu' },
      inLanguage: 'id-ID',
    });
    return () => { created.forEach(el => el.remove()); };
  }, []);

  // Translate article titles and excerpts when language or articles change
  useEffect(() => {
    if (lang === 'id' || articles.length === 0) { setTranslatedArticles({}); return; }
    let cancelled = false;
    const texts = {};
    articles.forEach(a => {
      texts[`title_${a.id}`] = a.title;
      if (a.excerpt) texts[`excerpt_${a.id}`] = a.excerpt;
      if (a.category) texts[`cat_${a.id}`] = a.category;
    });
    axios.post(`${BACKEND_URL}/api/translate/batch`, { texts, target_lang: lang })
      .then(res => { if (!cancelled) setTranslatedArticles(res.data.translations || {}); }).catch(() => {});
    return () => { cancelled = true; };
  }, [lang, articles]);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/articles?sort=newest&limit=50`);
      setArticles(res.data.articles);
      setExclusiveTeasers(res.data.exclusive_teasers || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchTrendingTags = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/tags/trending`);
      setTrendingTags(res.data.tags || []);
    } catch {}
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/search?q=${encodeURIComponent(q)}&limit=20`);
      setSearchResults(res.data.articles);
    } catch (err) { console.error(err); }
    finally { setSearching(false); }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/subscribe`, { email, source: 'blog', weekly_digest: weeklyDigest });
      setSubMsg(res.data.message); setEmail('');
    } catch (err) { setSubMsg(err.response?.data?.detail || t('general_error')); }
    finally { setSubmitting(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const displayArticles = searchResults !== null ? searchResults : articles;
  const featured = searchResults !== null ? [] : displayArticles.slice(0, 4);
  const [showAll, setShowAll] = useState(false);
  const older = searchResults !== null ? displayArticles : (showAll ? displayArticles.slice(4) : []);

  const sortedOlder = [...older].sort((a, b) => {
    switch (sortBy) {
      case 'oldest': return new Date(a.published_at) - new Date(b.published_at);
      case 'most_read': return (b.views || 0) - (a.views || 0);
      case 'most_liked': return (b.likes || 0) - (a.likes || 0);
      case 'most_comments': return (b.comment_count || 0) - (a.comment_count || 0);
      default: return new Date(b.published_at) - new Date(a.published_at);
    }
  });

  return (
    <div className="blog-page" data-testid="blog-page">
      {/* Header */}
      <header className="blog-header" data-testid="blog-header">
        <div className="blog-header-inner">
          <Link to="/" className="blog-logo">
            <TrendingUp size={28} />
            <div>
              <span className="blog-logo-title">Pakde Heru</span>
              <span className="blog-logo-sub">{t('hero_subtitle')}</span>
            </div>
          </Link>
          <nav className="blog-nav">
            <Link to="/" className="blog-nav-link active">{t('nav_blog')}</Link>
            <Link to="/buku" className="blog-nav-link">{t('nav_buku')}</Link>
            <a href="https://wa.me/628118007799?text=Halo%20Pakde%20Heru%2C%20saya%20ingin%20bergabung%20di%20komunitas!" target="_blank" rel="noopener noreferrer" className="blog-nav-link komunitas-link" data-testid="nav-komunitas"><Users size={16} /> {t('nav_komunitas')}</a>
            <div className="blog-social-links">
              <a href="https://www.instagram.com/pakdeheruu/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={18} /></a>
              <a href="https://www.facebook.com/pakdeheruu/" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} /></a>
              <a href="https://www.linkedin.com/in/herusidik" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><Linkedin size={18} /></a>
            </div>
            {user ? (
              <Link to={user.role === 'admin' ? '/admin' : '/member'} className="blog-subscribe-btn" style={{background:'rgba(217,251,6,0.15)',color:'#d9fb06',display:'flex',alignItems:'center',gap:'0.375rem'}} data-testid="nav-member-btn">
                <Crown size={16} /> {user.role === 'admin' ? 'Admin' : 'Member Area'}
              </Link>
            ) : (
              <Link to="/login" className="blog-subscribe-btn" style={{display:'flex',alignItems:'center',gap:'0.375rem'}} data-testid="nav-login-btn">
                <LogIn size={16} /> Masuk
              </Link>
            )}
            <button onClick={() => setShowSubscribe(true)} className="blog-subscribe-btn" data-testid="blog-subscribe-btn" style={{background:'transparent',border:'1px solid rgba(255,255,255,0.15)',color:'#A1A1AA'}}>
              <Mail size={16} /> {t('nav_subscribe')}
            </button>
          </nav>
          <LanguagePicker />
        </div>
      </header>

      {/* Hero */}
      <section className="blog-hero" data-testid="blog-hero">
        <div className="blog-hero-split">
          <div className="blog-hero-text">
            <h1 className="blog-hero-title" data-testid="blog-hero-title">{t('hero_subtitle')} <br /><span>Strategic Thinker</span></h1>
            <p className="blog-hero-desc">{t('hero_desc')}</p>
            <div className="blog-hero-pillars">
              <Link to="/tag/behavioral%20finance" className="blog-pillar blog-pillar-link" data-testid="pillar-behavioral-finance">
                <Brain size={20} />
                <div><strong>{t('pillar_bf_title')}</strong><span>{t('pillar_bf_desc')}</span></div>
                <ArrowRight size={16} className="pillar-arrow" />
              </Link>
              <Link to="/tag/wealth%20logic" className="blog-pillar blog-pillar-link" data-testid="pillar-wealth-logic">
                <TrendingUp size={20} />
                <div><strong>{t('pillar_wl_title')}</strong><span>{t('pillar_wl_desc')}</span></div>
                <ArrowRight size={16} className="pillar-arrow" />
              </Link>
              <Link to="/tag/manajemen%20risiko" className="blog-pillar blog-pillar-link" data-testid="pillar-risk-mindset">
                <Shield size={20} />
                <div><strong>{t('pillar_rm_title')}</strong><span>{t('pillar_rm_desc')}</span></div>
                <ArrowRight size={16} className="pillar-arrow" />
              </Link>
            </div>
            <div className="blog-hero-social">
              <a href="https://www.instagram.com/pakdeheruu/" target="_blank" rel="noopener noreferrer" className="blog-social-badge"><Instagram size={16} /> @pakdeheruu</a>
              <a href="https://www.facebook.com/pakdeheruu/" target="_blank" rel="noopener noreferrer" className="blog-social-badge"><Facebook size={16} /> Pakde Heru</a>
              <a href="https://www.linkedin.com/in/herusidik" target="_blank" rel="noopener noreferrer" className="blog-social-badge"><Linkedin size={16} /> Heru Sidik</a>
            </div>
          </div>
          <div className="blog-hero-portrait">
            <div className="blog-hero-portrait-glow"></div>
            <img src={`${process.env.PUBLIC_URL}/pakdeheru-hero.png`} alt="Pakde Heru" />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="blog-articles-section">
        <div className="blog-articles-inner">
          {/* Search Bar */}
          <div className="blog-search-bar" data-testid="blog-search-bar">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder={t('search_placeholder')}
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              data-testid="search-input"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => { setSearchQuery(''); setSearchResults(null); }} data-testid="search-clear"><X size={16} /></button>
            )}
          </div>

          {searchResults !== null && (
            <div className="search-result-info" data-testid="search-results-info">
              <p>{searching ? t('searching') : `${searchResults.length} ${t('articles_found')} "${searchQuery}"`}</p>
            </div>
          )}

          {/* Trending Tags */}
          {trendingTags.length > 0 && !searchResults && (
            <div className="trending-tags-section" data-testid="trending-tags">
              <div className="trending-tags-header">
                <Flame size={16} className="trending-flame" />
                <span>Trending Minggu Ini</span>
              </div>
              <div className="trending-tags-list">
                {trendingTags.map((tag, i) => (
                  <Link
                    to={`/tag/${encodeURIComponent(tag.name)}`}
                    key={tag.name}
                    className="trending-tag-chip"
                    data-testid={`trending-tag-${i}`}
                  >
                    <span className="trending-tag-rank">#{i + 1}</span>
                    <span className="trending-tag-name">{tag.name}</span>
                    <span className="trending-tag-count">{tag.count} artikel</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="blog-loading">{t('loading_articles')}</div>
          ) : displayArticles.length === 0 && searchResults !== null ? (
            <div className="blog-empty" data-testid="search-empty">
              <Search size={48} />
              <h3>{t('no_results')}</h3>
              <p>{t('try_other_keywords')} <button className="link-btn" onClick={() => { setSearchQuery(''); setSearchResults(null); }}>{t('view_all_articles')}</button></p>
            </div>
          ) : articles.length === 0 ? (
            <div className="blog-empty" data-testid="blog-empty">
              <Brain size={48} />
              <h3>{t('articles_coming_soon')}</h3>
              <p>{t('articles_coming_desc')}</p>
              <button onClick={() => setShowSubscribe(true)} className="blog-cta-btn"><Mail size={18} /> {t('subscribe_for_updates')}</button>
            </div>
          ) : (
            <>
              {/* Featured 3 */}
              {featured.length > 0 && (
                <div className="blog-section-label" data-testid="featured-section">
                  <h2>{t('latest_articles')}</h2>
                </div>
              )}
              {featured.length > 0 && (
                <div className="blog-featured-grid">
                  {featured.map((article) => (
                    <ArticleCard key={article.id} article={article} formatDate={formatDate} translatedArticles={translatedArticles} />
                  ))}
                </div>
              )}

              {/* Show All button */}
              {!showAll && !searchResults && articles.length > 4 && (
                <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                  <button
                    onClick={() => setShowAll(true)}
                    data-testid="show-all-articles-btn"
                    style={{
                      background: 'rgba(217,251,6,0.1)', border: '1px solid rgba(217,251,6,0.3)',
                      color: '#d9fb06', borderRadius: '2rem', padding: '0.75rem 2rem',
                      fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {t('view_all_articles') || 'Lihat Semua Artikel'} ({articles.length}) <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* Exclusive Member Teasers */}
              {exclusiveTeasers.length > 0 && !searchResults && (
                <div className="blog-exclusive-section" style={{ marginBottom: '2rem' }} data-testid="exclusive-teasers">
                  <div className="blog-section-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Crown size={18} color="#d9fb06" /> {t('exclusive_for_members') || 'Eksklusif untuk Member'}
                    </h2>
                  </div>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {exclusiveTeasers.map(teaser => (
                      <div key={teaser.id} style={{
                        background: 'rgba(217,251,6,0.03)', border: '1px solid rgba(217,251,6,0.15)',
                        borderRadius: '0.75rem', padding: '1.25rem', position: 'relative', overflow: 'hidden',
                      }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                          {teaser.featured_image && (
                            <img src={resolveImageUrl(teaser.featured_image)} alt="" style={{ width: '80px', height: '80px', borderRadius: '0.5rem', objectFit: 'cover', flexShrink: 0, filter: 'blur(1px)', opacity: 0.7 }} />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                              <span style={{ padding: '2px 8px', borderRadius: '8px', fontSize: '0.65rem', fontWeight: 700, background: 'rgba(217,251,6,0.15)', color: '#d9fb06', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Member Only
                              </span>
                              {teaser.category && <span style={{ fontSize: '0.7rem', color: '#71717A' }}>{teaser.category}</span>}
                              {teaser.exclusive_until && (
                                <span style={{ fontSize: '0.65rem', color: '#71717A' }}>
                                  Publik mulai {new Date(teaser.exclusive_until).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </div>
                            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.05rem', color: '#EDEDED', marginBottom: '0.375rem', lineHeight: 1.3 }}>
                              {teaser.title}
                            </h3>
                            <p style={{ fontSize: '0.8rem', color: '#71717A', lineHeight: 1.4, marginBottom: '0.5rem' }}>
                              {teaser.excerpt}
                            </p>
                            <Link to={user ? `/blog/${teaser.id}` : '/register'} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                              fontSize: '0.8rem', fontWeight: 600, color: '#d9fb06', textDecoration: 'none',
                            }} data-testid={`exclusive-cta-${teaser.id}`}>
                              {user ? 'Baca Artikel' : 'Daftar Gratis untuk Membaca'} <ArrowRight size={14} />
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Older Articles */}
              {sortedOlder.length > 0 && (
                <>
                  <div className="blog-section-label blog-section-older" data-testid="older-section">
                    <h2>{searchResults !== null ? t('search_results') : t('all_articles')}</h2>
                    <div className="blog-sort-bar">
                      <SortAsc size={16} />
                      {[
                        ['newest', t('sort_newest')],
                        ['oldest', t('sort_oldest')],
                        ['most_read', t('sort_popular')],
                        ['most_liked', t('sort_liked')],
                        ['most_comments', t('sort_comments')],
                      ].map(([val, label]) => (
                        <button key={val} className={`sort-btn ${sortBy === val ? 'active' : ''}`} onClick={() => setSortBy(val)} data-testid={`sort-${val}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="blog-list">
                    {sortedOlder.map((article) => (
                      <ArticleListItem key={article.id} article={article} formatDate={formatDate} translatedArticles={translatedArticles} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* Book Promo */}
      <section className="blog-promo">
        <div className="blog-promo-inner">
          <img src={`${BACKEND_URL}/api/static/book-cover.png`} alt="Atomic Habits for Sharia Bankers" className="blog-promo-cover" />
          <div className="blog-promo-text">
            <span className="blog-promo-label">{t('book_latest')}</span>
            <h3>Atomic Habits for Sharia Bankers</h3>
            <p>{t('book_desc')}</p>
            <Link to="/buku" className="blog-promo-btn">{t('book_more')} <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="blog-footer">
        <div className="blog-footer-inner">
          <p>&copy; 2026 {t('footer_copyright')}</p>
          <div className="blog-footer-links">
            <a href="https://www.instagram.com/pakdeheruu/" target="_blank" rel="noopener noreferrer">Instagram</a>
            <a href="https://www.facebook.com/pakdeheruu/" target="_blank" rel="noopener noreferrer">Facebook</a>
            <a href="https://www.linkedin.com/in/herusidik" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>
        </div>
      </footer>

      {/* Subscribe Popup with Weekly Digest opt-in */}
      {showSubscribe && (
        <div className="newsletter-popup-overlay" onClick={() => setShowSubscribe(false)}>
          <div className="newsletter-popup" onClick={e => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setShowSubscribe(false)}><X size={20} /></button>
            <Mail size={32} className="popup-icon" />
            <h3 className="popup-title">{t('subscribe_title')}</h3>
            <p className="popup-desc">{t('subscribe_desc')}</p>
            {!subMsg ? (
              <form onSubmit={handleSubscribe} className="popup-form">
                <input type="email" placeholder={t('subscribe_email_placeholder')} value={email} onChange={e => setEmail(e.target.value)} required disabled={submitting} data-testid="subscribe-email-input" />
                <label className="digest-checkbox" data-testid="weekly-digest-checkbox">
                  <input type="checkbox" checked={weeklyDigest} onChange={e => setWeeklyDigest(e.target.checked)} />
                  <span>{t('subscribe_weekly_digest')}</span>
                </label>
                <button type="submit" disabled={submitting} data-testid="subscribe-submit-btn">{submitting ? t('subscribe_submitting') : t('subscribe_button')} <ArrowRight size={18} /></button>
              </form>
            ) : (
              <div className="popup-success"><p>{subMsg}</p></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Mini share buttons for article previews
const MiniShare = ({ title, articleId }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/api/share/${articleId}`;
  const articleUrl = `${window.location.origin}/blog/${articleId}`;
  const openShare = (url, e) => { e.stopPropagation(); e.preventDefault(); window.open(url, '_blank', 'noopener'); };
  return (
    <div className="mini-share" onClick={e => e.preventDefault()} data-testid={`mini-share-${articleId}`}>
      <button className="mini-share-btn mini-wa" onClick={e => openShare(`https://wa.me/?text=${encodeURIComponent(title + '\n' + shareUrl)}`, e)} title="WhatsApp">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.633-1.46A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.214 0-4.26-.723-5.916-1.948l-.424-.317-2.75.867.832-2.685-.348-.463A9.716 9.716 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
      </button>
      <button className="mini-share-btn mini-tw" onClick={e => openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`, e)} title="X / Twitter">
        <Twitter size={12} />
      </button>
      <button className="mini-share-btn mini-fb" onClick={e => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, e)} title="Facebook">
        <Facebook size={12} />
      </button>
      <button className={`mini-share-btn mini-copy ${copied ? 'copied' : ''}`} onClick={e => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(articleUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }} title="Copy Link">
        {copied ? <Check size={12} /> : <LinkIcon size={12} />}
      </button>
    </div>
  );
};

// Featured article card with image
const ArticleCard = ({ article, featured, formatDate, translatedArticles = {} }) => {
  const navigate = useNavigate();
  const tTitle = translatedArticles[`title_${article.id}`] || article.title;
  const tExcerpt = translatedArticles[`excerpt_${article.id}`] || article.excerpt;
  const tCat = translatedArticles[`cat_${article.id}`] || article.category;
  return (
    <Link to={`/blog/${article.id}`} className={`blog-card ${featured ? 'blog-card-featured' : ''}`} data-testid={`article-${article.id}`}>
      {article.featured_image && (
        <div className="blog-card-img">
          <img src={resolveImageUrl(article.featured_image)} alt={tTitle} loading="lazy" />
        </div>
      )}
      <div className="blog-card-body">
        {tCat && <span className="blog-card-cat">{tCat}</span>}
        <h2 className="blog-card-title">{tTitle}</h2>
        <p className="blog-card-excerpt">{tExcerpt}</p>
        <div className="blog-card-meta">
          <span><Calendar size={14} /> {formatDate(article.published_at)}</span>
          <span><Eye size={14} /> {article.views || 0}</span>
          <span><Heart size={14} /> {article.likes || 0}</span>
          <span><MessageSquare size={14} /> {article.comment_count || 0}</span>
        </div>
        {article.tags && (
          <div className="blog-card-tags">
            {(article.tags).slice(0, 3).map((tag, i) => (
              <span key={i} className="blog-tag blog-tag-link" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/tag/${encodeURIComponent(tag)}`); }}><Tag size={12} /> {tag}</span>
            ))}
          </div>
        )}
        <div className="blog-card-bottom">
          <span className="blog-card-read">{t('btn_read_more')} <ArrowRight size={16} /></span>
          <MiniShare title={article.title} articleId={article.id} />
        </div>
      </div>
    </Link>
  );
};

// List item with thumbnail
const ArticleListItem = ({ article, formatDate, translatedArticles = {} }) => {
  const navigate = useNavigate();
  const tTitle = translatedArticles[`title_${article.id}`] || article.title;
  const tExcerpt = translatedArticles[`excerpt_${article.id}`] || article.excerpt;
  const tCat = translatedArticles[`cat_${article.id}`] || article.category;
  return (
    <Link to={`/blog/${article.id}`} className="blog-list-item" data-testid={`list-${article.id}`}>
      {article.featured_image && (
        <div className="blog-list-img">
          <img src={resolveImageUrl(article.featured_image)} alt={tTitle} loading="lazy" />
        </div>
      )}
      <div className="blog-list-body">
        {tCat && <span className="blog-list-cat">{tCat}</span>}
        <h3 className="blog-list-title">{tTitle}</h3>
        <p className="blog-list-excerpt">{tExcerpt}</p>
        {article.tags && (
          <div className="blog-card-tags" style={{ marginTop: '0.5rem' }}>
            {(article.tags).slice(0, 3).map((tag, i) => (
              <span key={i} className="blog-tag blog-tag-link" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/tag/${encodeURIComponent(tag)}`); }}><Tag size={12} /> {tag}</span>
            ))}
          </div>
        )}
      </div>
      <div className="blog-list-stats">
        <span><Calendar size={13} /> {formatDate(article.published_at)}</span>
        <span><Eye size={13} /> {article.views || 0}</span>
        <span><Heart size={13} /> {article.likes || 0}</span>
        <span><MessageSquare size={13} /> {article.comment_count || 0}</span>
        <MiniShare title={article.title} articleId={article.id} />
      </div>
    </Link>
  );
};

export default BlogPage;
